/**
 * TW: Sync ingredients hero + content panels from variant metafields / PDP gallery.
 * Badge image stays static (theme editor); never updated here.
 */
window.TwIngredientsVariantSync = (function () {
  'use strict';

  const MEDIA_SCRIPT_SELECTOR = 'script[data-tw-ingredients-variant-media-json]';
  const SECTION_SELECTOR = '[data-tw-ingredients-section]';
  const latestContentRequestBySection = {};

  function parseMediaConfigs() {
    const configs = [];
    document.querySelectorAll(MEDIA_SCRIPT_SELECTOR).forEach(function (script) {
      try {
        const data = JSON.parse(script.textContent);
        if (data) configs.push(data);
      } catch (e) {
        console.warn('[TwIngredientsVariantSync] Invalid JSON', e);
      }
    });
    return configs;
  }

  function payloadFromElement(source) {
    if (!source) return null;
    const out = {};
    const hero = source.getAttribute('data-tw-ingredients-hero');
    const gradientStart = source.getAttribute('data-tw-ingredients-gradient-start');
    const gradientEnd = source.getAttribute('data-tw-ingredients-gradient-end');
    const ctaColor = source.getAttribute('data-tw-ingredients-cta-color');
    const heroAlt = source.getAttribute('data-tw-ingredients-hero-alt');

    if (hero) out.hero = hero.trim();
    if (gradientStart) out.gradientStart = gradientStart.trim();
    if (gradientEnd) out.gradientEnd = gradientEnd.trim();
    if (ctaColor) out.ctaColor = ctaColor.trim();
    if (heroAlt) out.heroAlt = heroAlt.trim();

    return Object.keys(out).length > 0 ? out : null;
  }

  function getVariantPayload(config, variantId, scentKey) {
    const defaults = config.defaults || {};
    const id = variantId != null ? String(variantId) : '';

    if (scentKey && config.scents && config.scents[scentKey]) {
      return Object.assign({}, defaults, config.scents[scentKey]);
    }
    if (id && config.variants && config.variants[id]) {
      return Object.assign({}, defaults, config.variants[id]);
    }
    return defaults;
  }

  function getSectionRoots(config) {
    const roots = [];
    if (config.sectionId) {
      const byId = document.getElementById('shopify-section-' + config.sectionId);
      if (byId) roots.push(byId);
    }
    document.querySelectorAll(SECTION_SELECTOR).forEach(function (el) {
      if (!config.sectionId || el.getAttribute('data-section-id') === config.sectionId) {
        const wrap = el.closest('.shopify-section') || el.parentElement;
        if (wrap && roots.indexOf(wrap) === -1) roots.push(wrap);
      }
    });
    return roots;
  }

  function getGalleryHeroUrl(mainSectionId) {
    const main = mainSectionId
      ? document.getElementById('MainProduct-' + mainSectionId)
      : document.querySelector('[id^="MainProduct-"]');
    if (!main) return '';

    const activeItem = main.querySelector('.product__media-item.is-active');
    const img =
      (activeItem && activeItem.querySelector('img')) ||
      main.querySelector('.product__media-item img') ||
      main.querySelector('.product__media img');

    return img ? img.currentSrc || img.src || '' : '';
  }

  function applyHeroToRoot(root, payload) {
    if (!root || !payload) return false;

    const panel = root.querySelector('[data-tw-ingredients-panel]');
    if (!panel) return false;

    const heroImg = panel.querySelector('[data-tw-ingredients-hero]');
    const heroWrap = panel.querySelector('[data-tw-ingredients-hero-wrap]');
    const heroPlaceholder = panel.querySelector('[data-tw-ingredients-hero-placeholder]');
    const heroUrl = payload.hero || '';

    if (heroImg && heroUrl) {
      heroImg.src = heroUrl;
      heroImg.removeAttribute('srcset');
      heroImg.removeAttribute('sizes');
      if (payload.heroAlt) heroImg.alt = payload.heroAlt;
      heroImg.hidden = false;
    } else if (heroImg) {
      heroImg.hidden = true;
    }

    if (heroPlaceholder) heroPlaceholder.hidden = Boolean(heroUrl);
    if (heroWrap) heroWrap.setAttribute('aria-hidden', heroUrl ? 'false' : 'true');

    if (payload.gradientStart) {
      root.style.setProperty('--tw-ingredients-gradient-start', payload.gradientStart);
    }
    if (payload.gradientEnd) {
      root.style.setProperty('--tw-ingredients-gradient-end', payload.gradientEnd);
    }
    if (payload.ctaColor) {
      root.style.setProperty('--tw-ingredients-cta-color', payload.ctaColor);
      root.style.setProperty('--tw-ingredients-cta-hover', darkenHex(payload.ctaColor, 10));
    }

    return Boolean(heroUrl);
  }

  function darkenHex(hex, percent) {
    const raw = String(hex).replace('#', '').trim();
    if (raw.length !== 6) return hex;
    const r = Math.max(0, parseInt(raw.slice(0, 2), 16) - Math.round(2.55 * percent));
    const g = Math.max(0, parseInt(raw.slice(2, 4), 16) - Math.round(2.55 * percent));
    const b = Math.max(0, parseInt(raw.slice(4, 6), 16) - Math.round(2.55 * percent));
    return (
      '#' +
      [r, g, b]
        .map(function (n) {
          return n.toString(16).padStart(2, '0');
        })
        .join('')
    );
  }

  function applyPayloadToAllRoots(payload, config) {
    const roots = config ? getSectionRoots(config) : [];
    if (roots.length === 0) {
      document.querySelectorAll(SECTION_SELECTOR).forEach(function (el) {
        const wrap = el.closest('.shopify-section') || el;
        applyHeroToRoot(wrap, payload);
      });
      return;
    }
    roots.forEach(function (root) {
      applyHeroToRoot(root, payload);
    });
  }

  function normalizeScentKey(key) {
    if (!key) return '';
    return String(key)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function resolveActiveScentKey(scentKey, variantId) {
    if (scentKey) return normalizeScentKey(scentKey);

    if (variantId) {
      const btn = document.querySelector(
        '.tw-scent-dropdown__option[data-variant-id="' + variantId + '"]'
      );
      if (btn && btn.getAttribute('data-scent-key')) {
        return normalizeScentKey(btn.getAttribute('data-scent-key'));
      }
    }

    const selectedOption = document.querySelector(
      '.tw-scent-dropdown__option[aria-selected="true"]'
    );
    if (selectedOption && selectedOption.getAttribute('data-scent-key')) {
      return normalizeScentKey(selectedOption.getAttribute('data-scent-key'));
    }

    return '';
  }

  function fetchContentForVariant(variantId) {
    if (!variantId) return;

    const viewParam = new URLSearchParams(window.location.search).get('view');

    document.querySelectorAll(SECTION_SELECTOR).forEach(function (sectionEl) {
      const sectionId = sectionEl.getAttribute('data-section-id');
      if (!sectionId) return;

      const requestId = (latestContentRequestBySection[sectionId] || 0) + 1;
      latestContentRequestBySection[sectionId] = requestId;

      const fetchUrl = new URL(window.location.pathname, window.location.origin);
      fetchUrl.searchParams.set('variant', String(variantId));
      fetchUrl.searchParams.set('section_id', String(sectionId));
      if (viewParam) fetchUrl.searchParams.set('view', viewParam);

      fetch(fetchUrl.toString())
        .then(function (response) {
          if (!response.ok) throw new Error('Ingredients content fetch failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          if (latestContentRequestBySection[sectionId] !== requestId) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newContent = doc.querySelector('[data-tw-ingredients-content]');
          const currentContent = sectionEl.querySelector('[data-tw-ingredients-content]');
          if (!newContent || !currentContent) return;

          currentContent.innerHTML = newContent.innerHTML;

          const newHeading = currentContent.querySelector(
            '.tw-ingredients-panel__heading, .tw-section-heading'
          );
          if (newHeading && newHeading.id) {
            sectionEl.setAttribute('aria-labelledby', newHeading.id);
          }

          const wrapper = sectionEl.closest('.tw-ingredients-section-wrapper');
          if (wrapper) {
            delete wrapper.dataset.twIngredientsAccordionBound;
            wrapper.dispatchEvent(
              new CustomEvent('tw-ingredients:content-swapped', { bubbles: true })
            );
          }
        })
        .catch(function (err) {
          console.warn('[TwIngredientsVariantSync] Content fetch failed', err);
        });
    });
  }

  function syncContent(scentKey, variantId) {
    const activeKey = resolveActiveScentKey(scentKey, variantId);
    const activeVariantStr = variantId != null ? String(variantId) : '';

    document.querySelectorAll('[data-tw-ingredients-content]').forEach(function (container) {
      const panels = Array.from(
        container.querySelectorAll('[data-tw-ingredients-content-panel]')
      );
      if (!panels.length) return;

      if (panels.length === 1 && panels[0].getAttribute('data-scent-key') === 'default') return;

      let matched = false;
      let visiblePanel = null;

      if (activeKey) {
        panels.forEach(function (panel) {
          const key = normalizeScentKey(panel.getAttribute('data-scent-key') || '');
          const show = key === activeKey;
          panel.hidden = !show;
          if (show) {
            matched = true;
            visiblePanel = panel;
          }
        });
      }

      if (!matched && activeVariantStr) {
        panels.forEach(function (panel) {
          const vid = panel.getAttribute('data-variant-id') || '';
          const show = vid === activeVariantStr;
          panel.hidden = !show;
          if (show) {
            matched = true;
            visiblePanel = panel;
          }
        });
      }

      if (!matched && panels.length) {
        panels.forEach(function (p, i) {
          p.hidden = i !== 0;
        });
        visiblePanel = panels[0];
      }

      if (visiblePanel) {
        const heading = visiblePanel.querySelector('.tw-ingredients-panel__heading, .tw-section-heading');
        const sectionWrapper = container.closest('[data-tw-ingredients-section]');
        if (sectionWrapper && heading && heading.id) {
          sectionWrapper.setAttribute('aria-labelledby', heading.id);
        }
      }

      const sectionWrapper = container.closest('.tw-ingredients-section-wrapper');
      if (sectionWrapper) {
        delete sectionWrapper.dataset.twIngredientsAccordionBound;
        sectionWrapper.dispatchEvent(
          new CustomEvent('tw-ingredients:content-swapped', { bubbles: true })
        );
      }
    });
  }

  function hasSpecificHero(configs, variantId, scentKey) {
    return configs.some(function (config) {
      const payload = getVariantPayload(config, variantId, scentKey);
      return Boolean(payload && payload.hero);
    });
  }

  function sync(variantId, sourceElement, scentKey, mainSectionId) {
    const configs = parseMediaConfigs();
    const fromEl = payloadFromElement(sourceElement);
    const resolvedScentKey =
      scentKey || (sourceElement && sourceElement.getAttribute('data-scent-key'));
    const galleryUrl = hasSpecificHero(configs, variantId, resolvedScentKey)
      ? ''
      : getGalleryHeroUrl(mainSectionId);

    if (configs.length === 0) {
      const payload = Object.assign({}, fromEl || {}, galleryUrl ? { hero: galleryUrl } : {});
      if (payload.hero) applyPayloadToAllRoots(payload, null);
    } else {
      configs.forEach(function (config) {
        const fromJson = getVariantPayload(config, variantId, resolvedScentKey);
        const payload = Object.assign({}, fromJson, fromEl || {});
        if (!payload.hero && galleryUrl) payload.hero = galleryUrl;
        applyPayloadToAllRoots(payload, config);
      });
    }

    syncContent(resolvedScentKey, variantId);
    fetchContentForVariant(variantId);
  }

  /** After PDP gallery HTML refresh — match active product media image. */
  function syncHeroFromGallery(mainSectionId) {
    const galleryUrl = getGalleryHeroUrl(mainSectionId);
    if (!galleryUrl) return;

    const configs = parseMediaConfigs();
    const currentVariantId = (function () {
      const params = new URLSearchParams(window.location.search);
      let id = params.get('variant');
      if (!id) {
        const input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
        if (input && input.value) id = input.value;
      }
      return id;
    })();

    if (hasSpecificHero(configs, currentVariantId, null)) return;

    const payload = { hero: galleryUrl };
    configs.forEach(function (config) {
      applyPayloadToAllRoots(payload, config);
    });
    if (configs.length === 0) {
      applyPayloadToAllRoots(payload, null);
    }
  }

  function syncFromPageVariant(mainSectionId) {
    const params = new URLSearchParams(window.location.search);
    let variantId = params.get('variant');
    if (!variantId) {
      const input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (input && input.value) variantId = input.value;
    }
    sync(variantId, null, null, mainSectionId);
    syncHeroFromGallery(mainSectionId);
  }

  function bindVariantEvents() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      const variant = event && event.data && event.data.variant;
      if (variant && variant.id) {
        sync(String(variant.id), null, null, null);
      }
    });
  }

  function init() {
    syncFromPageVariant();
    bindVariantEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { sync, syncContent, syncHeroFromGallery, syncFromPageVariant };
})();
